import { useEffect, useState } from 'react';
import { Box, Column, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { type Control, useFormContext, type UseFormWatch } from 'react-hook-form';
import { TipBanner } from '~/components/shared/TipBanner';
import { useAutoConfirmDefaults } from '~/hooks/useAutoConfirmDefaults';
import { useLastMinuteAutoConfirm } from '~/hooks/useLastMinuteAutoConfirm';
import { PaymentLockedBanner } from '../common';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { AutoConfirmField, isAutoConfirmAvailable } from './AutoConfirmField';
import { InviteField } from './InviteField';
import { InviteSubCard } from './InviteSubCard';

interface ISpeedUpCoverageSectionProps {
  control: Control<IShiftDetailsFormValues>;
  watch: UseFormWatch<IShiftDetailsFormValues>;
}

const INVITE_TOOLTIP = (
  <Typography as="span" bold color={colors.shades[0]}>
    Please note, invites will not be delivered to professionals on requested dates in which they are
    unavailable.
  </Typography>
);

export const SpeedUpCoverageSection = ({ control, watch }: ISpeedUpCoverageSectionProps) => {
  const professionType = watch('professionType');
  const preferredSpecialty = watch('preferredSpecialty');
  const officeId = watch('officeId');
  const dates = watch('dates');
  const arrivalTime = watch('arrivalTime');
  const shiftOverrides = watch('shiftOverrides');

  const { setValue } = useFormContext<IShiftDetailsFormValues>();
  const {
    isPaymentLocked: paymentLocked,
    favoritesLoading,
    favoritesDisabled,
    defaultAutoConfirm,
  } = useAutoConfirmDefaults(officeId);

  const {
    shouldForceOn: shouldForceLastMinuteOn,
    exposeExperiment: exposeLastMinuteExperiment,
  } = useLastMinuteAutoConfirm(officeId, dates, arrivalTime, shiftOverrides);

  // `paymentLocked` is the shared, loading-aware gate from usePaymentEligibility (via
  // useAutoConfirmDefaults): it stays unlocked while the payment query resolves so the
  // payment-locked banner never flashes, and locks only once the office is known unable to pay.
  const autoConfirmAvailable = isAutoConfirmAvailable(professionType, preferredSpecialty);

  // The payment lock wins: an office that cannot pay has Auto-Confirm cleared and disabled
  // regardless of how soon the shift starts.
  const lastMinuteLocked = shouldForceLastMinuteOn && !paymentLocked && autoConfirmAvailable;

  // Tracks whether the invite list has no professionals. When locked, the invite
  // card stays active in the empty state (nothing to interact with) but is dimmed
  // when there is a populated list to lock.
  const [inviteEmpty, setInviteEmpty] = useState(false);

  // When payment becomes invalid, the invite list and auto-confirm are gated.
  // Clear any selection / auto-confirm captured beforehand so no stale, locked
  // values are submitted. Only runs while locked, never overriding user input
  // once payment is valid again.
  useEffect(() => {
    if (!paymentLocked) return;
    setValue('invite', [], { shouldDirty: false });
    setValue('inviteNames', {}, { shouldDirty: false });
    setValue(
      'autoConfirm',
      { favorites: false, eligibleProfessionals: false },
      { shouldDirty: false },
    );
  }, [paymentLocked, setValue]);

  // Last-minute experiment exposure: only offices that can pay and reach an available
  // Auto-Confirm section are exposed, so offices that could never be enforced don't dilute it.
  useEffect(() => {
    if (paymentLocked || !autoConfirmAvailable) return;
    exposeLastMinuteExperiment();
  }, [paymentLocked, autoConfirmAvailable, exposeLastMinuteExperiment]);

  // Last-minute enforcement (GOTU-5399): no dirty guard, because the office is not allowed to
  // opt out. Moving the shift out of the window unlocks the toggles but leaves the values, so
  // nothing flips underneath the office.
  useEffect(() => {
    if (!lastMinuteLocked || favoritesLoading) return;
    setValue('autoConfirm', defaultAutoConfirm, { shouldDirty: false });
  }, [lastMinuteLocked, favoritesLoading, defaultAutoConfirm, setValue]);

  return (
    <Column data-testid="speed-up-coverage-section" data-cy="speed-up-coverage-section" gap={3}>
      {paymentLocked ? (
        <Box style={{ marginTop: -8 }}>
          <PaymentLockedBanner context="invite" />
        </Box>
      ) : (
        <Box style={{ marginTop: -8 }}>
          <TipBanner
            visible
            description="Secure coverage with ease! Shifts with invites fill 2x faster."
          />
        </Box>
      )}

      <InviteSubCard title="Auto-Confirm:" testId="auto-confirm-sub-card" disabled={paymentLocked}>
        <AutoConfirmField
          control={control}
          professionType={professionType}
          preferredSpecialty={preferredSpecialty}
          eventContext="shift_request"
          favoritesDisabled={favoritesDisabled}
          hideHeader
          disabled={paymentLocked}
          lockedOn={lastMinuteLocked}
        />
      </InviteSubCard>

      <InviteSubCard
        title="Invite Professionals:"
        tooltip={INVITE_TOOLTIP}
        testId="invite-sub-card"
        disabled={paymentLocked && !inviteEmpty}
      >
        {/*
          `disabled` and `compact` are two facets of the same payment-locked state here —
          the list is both made non-interactive and shrunk so the CTA clears the fold — so
          both are intentionally driven by `paymentLocked`. They stay separate props (rather
          than one combined flag) because the invite-list stack supports each independently.
        */}
        <InviteField
          control={control}
          professionType={professionType}
          officeId={officeId}
          disabled={paymentLocked}
          compact={paymentLocked}
          onListEmptyChange={setInviteEmpty}
        />
      </InviteSubCard>
    </Column>
  );
};
